import React from 'react';
import { MapPin, Check, Clock } from 'lucide-react';
import { locations } from '../data/mock';

const LocationsSection = () => {
  const activeLocations = locations.filter(l => l.active);
  const comingSoon = locations.filter(l => !l.active);

  return (
    <section id="locations" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block px-4 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-full mb-4">
            Costa del Sol
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Where to find us<span className="text-blue-500">.</span>
          </h2>
          <p className="text-xl text-slate-600">
            Our vending machines are located at premium venues across the Costa del Sol's most vibrant nightlife destinations.
          </p>
        </div>

        {/* Map Visual */}
        <div className="relative mb-16">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 lg:p-12 overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Costa del Sol Coverage</h3>
                  <p className="text-slate-400 text-sm">Premium venue locations</p>
                </div>
              </div>

              {/* Active Locations */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {activeLocations.map((location) => (
                  <div
                    key={location.id}
                    className="group bg-white/10 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 border border-white/10"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-lg">{location.city}</h4>
                        <p className="text-slate-400 text-sm mt-1">{location.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coming Soon */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Expanding Soon</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {comingSoon.map((location) => (
                    <span
                      key={location.id}
                      className="px-4 py-2 bg-white/5 text-slate-400 rounded-full text-sm border border-white/10"
                    >
                      {location.city}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
              <div className="absolute top-10 right-10 w-40 h-40 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-10 right-20 w-60 h-60 border border-white rounded-full"></div>
              <div className="absolute top-1/2 right-0 w-80 h-80 border border-white/50 rounded-full -translate-y-1/2"></div>
            </div>
          </div>
        </div>

        {/* Venue Types */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: '🍸', name: 'Bars & Pubs', desc: 'Late-night hotspots' },
            { icon: '🌴', name: 'Beach Clubs', desc: 'Coastal venues' },
            { icon: '🎉', name: 'Nightclubs', desc: 'Party destinations' },
            { icon: '🎪', name: 'Festivals', desc: 'Special events' }
          ].map((venue, index) => (
            <div
              key={index}
              className="text-center p-6 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors duration-300"
            >
              <div className="text-4xl mb-4">{venue.icon}</div>
              <h4 className="font-semibold text-slate-900 mb-1">{venue.name}</h4>
              <p className="text-slate-500 text-sm">{venue.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
